/**
 * localStorage shim that carries a shared-slip's destination (player + market + book)
 * across the unauthed signup → checkout → app boundary.
 *
 * Parallel to but distinct from `shareIdStorage` — that one persists the
 * share_id for sharer-attribution; this one persists the *where to land*
 * so a new user finishes checkout and immediately sees the line-movement
 * overlay for the exact pick they clicked, instead of a generic app shell.
 *
 * Consumed by PlayerSelectionView on mount when the URL has no slip params.
 * Cleared after first use.
 */

const SLIP_DESTINATION_TTL_MS = 60 * 60 * 1000; // 1h — covers a leisurely signup+checkout

const KEY = 'slip_destination';

/**
 * Coerce an unknown into `'over' | 'under'`, returning `undefined` for
 * anything else. The slip side is the only `'over' | 'under'` token that
 * crosses untyped boundaries (URL params, localStorage round-trips), so a
 * single coercer covers all three callsites: the signup-page URL-param
 * forwarding, the localStorage hydrator below, and PlayerSelectionView's
 * URL-param consumer.
 */
export function parseSlipSide(x: unknown): 'over' | 'under' | undefined {
  return x === 'over' || x === 'under' ? x : undefined;
}

export interface SlipDestination {
  /** Sport route segment — `'nba'` or `'mlb'`. */
  sport: string;
  /** Player UUID (from `current_player_odds.player_id`). */
  playerId: string;
  /** Market code (e.g. `'pts'`, `'pitcher_strikeouts'`). */
  market: string;
  /** Bookmaker key (e.g. `'fanduel'`). Optional — drives `clickedBook` highlight. */
  book?: string;
  /** Side the sharer picked. Optional — when present, the overlay opens on this
   *  specific (book, side) instead of `pickDefaultLine`'s "higher-EV side at
   *  the clicked book" fallback. Mismatches happen when the under is the
   *  better EV at the shared book but the sharer picked the over. */
  side?: 'over' | 'under';
}

export function storeSlipDestination(d: SlipDestination): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(KEY, JSON.stringify({ ...d, ts: Date.now() }));
}

export function getStoredSlipDestination(): SlipDestination | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SlipDestination & { ts?: number };
    if (!parsed.ts || Date.now() - parsed.ts > SLIP_DESTINATION_TTL_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    if (!parsed.sport || !parsed.playerId || !parsed.market) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return {
      sport: parsed.sport,
      playerId: parsed.playerId,
      market: parsed.market,
      book: parsed.book,
      side: parseSlipSide(parsed.side),
    };
  } catch {
    window.localStorage.removeItem(KEY);
    return null;
  }
}

export function clearStoredSlipDestination(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(KEY);
}
